import { html } from 'lit';

import { triggerAfsRequest } from '@client/adsense/index.js';
import { AdVendor } from '@client/core/AdVendor.js';
import { $config } from '@client/core/atoms/config.js';
import { isFeatureEnabled } from '@client/core/atoms/unleashFeatures.js';
import {
  type AdConfigPlacement,
  AdServer,
  AdSlot,
  type AfsConfig,
  type AfsPlacementId,
  type AfsQuery,
  DeviceType,
  type FinnLegendaryAfsPlacementId,
  UNLEASH_FEATURE_NAME,
  debugLog
} from '@schibsted-nmp/advertising-shared';

import getAfsChannel from './utils/getAfsChannel.js';
import { getExperimentQuery } from './utils/getExperimentQuery.js';
import { $afsAtom } from '../core/atoms/afs.js';

export class AfsAdVendor extends AdVendor {
  declare placementId: AfsPlacementId | FinnLegendaryAfsPlacementId;

  /**
   * This data is sent down as a param from the Podium Layout Server, and is used to
   * determine the query to be used for the AFS request. It will be replaced by server values
   * eventually.
   */
  afsQuery?: AfsQuery;

  constructor(placementId: string, afsQuery?: AfsQuery) {
    super(
      AdServer.Afs,
      placementId as AfsPlacementId | FinnLegendaryAfsPlacementId
    );

    if (afsQuery) this.afsQuery = afsQuery;
  }

  override updated() {
    // do nothing
  }

  override cleanupEvents() {
    // do nothing
  }

  override setupEvents() {
    this.cleanupEvents = $afsAtom.subscribe((afs) => {
      if (afs.config) {
        this.triggerAfsRequest({
          afsConfig: afs.config,
          placements: afs.placements
        });
      }
    });
  }

  private triggerAfsRequest({
    afsConfig,
    placements
  }: {
    afsConfig: AfsConfig;
    placements: AdConfigPlacement[];
  }): void {
    const afsPlacementConfig = placements.find(
      (placement) => placement.placementId === this.placementId
    )?.adServer.config;

    if (!afsPlacementConfig) {
      debugLog(`No AFS config found for placement: ${this.placementId}`);

      return;
    }

    const styleId =
      'styleId' in afsPlacementConfig ? afsPlacementConfig.styleId : undefined;

    if (!styleId) {
      debugLog(`No styleId found for placement: ${this.placementId}`);
      return;
    }

    const overrideAfsPubId = isFeatureEnabled(
      UNLEASH_FEATURE_NAME.overrideAfsPubId
    );

    const overrideConfig = overrideAfsPubId
      ? {
          pubId: 'partner-pub-6408936348506194',
          styleId:
            $config.get()?.deviceType === DeviceType.Desktop
              ? '5268347338'
              : '3955265035'
        }
      : {};

    const isEnabledAfsForNonConsent = isFeatureEnabled(
      UNLEASH_FEATURE_NAME.afsForNonConsent
    );

    // See developers.google.com/custom-search-ads/web/reference
    const afsPageOptions = {
      adPage: Number.parseInt(afsConfig.adPage, 10),
      pubId: afsConfig.pubId,
      query: getExperimentQuery(afsConfig.query),
      styleId,
      number: 1,
      ivt: !isEnabledAfsForNonConsent, // invalid traffic-only cookie
      channel: getAfsChannel(this.placementId),
      ...overrideConfig
    };

    if (styleId) {
      triggerAfsRequest(this.placementId, afsPageOptions);
    }
  }

  override requestAd() {
    // AFS is triggered by the config event listener, do nothing here
  }

  override render() {
    return html`
      <slot
        name="${AdSlot.fromPlacementId(this.placementId)}"
        id="${this.placementId}"
      ></slot>
    `;
  }
}
